import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { EditPencil, NavArrowDown } from 'iconoir-react'
import { isEqual, startCase } from 'lodash'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { useControlField, useField, useFormContext } from 'remix-validated-form'
import { CopyToClipboard } from '../shared/CopyToClipboard'
import { Tooltip } from '../Tooltip'
import Combo from 'app/components/ui/combo/combo'
import ComboAsync from 'app/components/ui/combo/combo-async'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { OptionType } from 'types/OptionType'

// Style constants matching ContactSpotlight
const WRAPPER_CLASS = `flex h-8 items-center gap-x-1`
const LABEL_CLASS = `text-body-xs text-subtle truncate`
const INPUT_EDIT_CLASS = `h-[20px] w-full cursor-pointer border-none bg-transparent px-0 text-left text-ui-xs font-semibold text-strong`
// Combo wraps a custom trigger in `bg-surface-raised`, which on a dark row reads as a permanent hover.
const COMBO_TRIGGER_CLASS = `bg-transparent`
const DEFAULT_EMPTY_VALUE = `—`

// Type definition matching original TypedFormField
export type TypedFormFieldV2<T> = {
  field: Extract<keyof T, string> | `isQualifiedLead` | `isMarkedNonPatient`
  required?: boolean
  options?: OptionType[]
  readonly?: boolean
  nullable?: boolean
  onClick?: () => void
  isLocked?: boolean
  useSingleFieldInput?: boolean
} & (
  | {
      isArray: true
      arrayFieldName: string
      arrayIndex: number
    }
  | {
      isArray?: false
      arrayFieldName?: string
      arrayIndex?: number
    }
)

/**
 * ReadOnly component for displaying non-editable field values
 */
export function TypedReadOnlyV2<T>({
  field,
  label,
  value,
  emptyMessage,
  isDate,
  isPhone,
  canCopy,
  className,
  isSpotlight = false,
}: {
  field: Extract<keyof T, string>
  label?: string
  value: any
  emptyMessage?: string
  isDate?: boolean
  isPhone?: boolean
  canCopy?: boolean
  className?: string
  isSpotlight?: boolean
}): ReactElement {
  const displayLabel = label || startCase(field)
  const labelWidth = isSpotlight ? `w-[147px]` : `w-[131px]`
  // Convert value to string for display
  let displayValue = isDate
    ? value
      ? formatDate({ value: value, dateOnly: true })
      : emptyMessage || DEFAULT_EMPTY_VALUE
    : isPhone
      ? value
        ? formatPhoneNumber(value)
        : emptyMessage || DEFAULT_EMPTY_VALUE
      : typeof value === `object` && value !== null
        ? JSON.stringify(value)
        : value || emptyMessage || DEFAULT_EMPTY_VALUE
  const [tooltipContent, setTooltipContent] = useState<string | undefined>(undefined)
  const [labelTooltipContent, setLabelTooltipContent] = useState<string | undefined>(undefined)

  return (
    <div className={WRAPPER_CLASS}>
      <Tooltip
        className={`${labelWidth} min-w-0`}
        tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        message={labelTooltipContent}
        placement="left"
      >
        <div
          className={LABEL_CLASS}
          onMouseOver={(event) => {
            const target = event.currentTarget
            const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
            setLabelTooltipContent(isOverflowing ? displayLabel : undefined)
          }}
          onMouseOut={() => setLabelTooltipContent(undefined)}
        >
          {displayLabel}
        </div>
      </Tooltip>
      <div className="flex min-w-0 flex-1 items-center justify-between pl-2">
        <Tooltip
          className="min-w-0 flex-1"
          tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
          message={tooltipContent}
        >
          <div
            className={classNames(`truncate text-ui-xs text-subtle`, className)}
            onMouseOver={(event) => {
              const target = event.currentTarget
              const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
              setTooltipContent(isOverflowing ? displayValue : undefined)
            }}
            onMouseOut={() => setTooltipContent(undefined)}
          >
            {displayValue}
          </div>
        </Tooltip>
        {canCopy && value && (
          <div className="h-4 w-4 shrink-0">
            <CopyToClipboard text={String(value)} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * InputType component for text input fields with inline editing
 */
export function TypedInputV2<T>({
  field,
  label,
  value,
  emptyMessage,
  type = `text`,
  canEdit = true,
  className,
  onChange,
  isSpotlight = false,
}: {
  field: Extract<keyof T, string>
  label?: string
  value: any
  emptyMessage?: string
  type?: string
  canEdit?: boolean
  className?: string
  onChange?: (value: string) => void
  isSpotlight?: boolean
}): ReactElement {
  const displayLabel = label || startCase(field)
  const labelWidth = isSpotlight ? `w-[147px]` : `w-[131px]`
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || ``)
  const [labelTooltipContent, setLabelTooltipContent] = useState<string | undefined>(undefined)
  const [tooltipContent, setTooltipContent] = useState<string | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)

  // Form integration
  const { defaultValue } = useField(field)
  const [formValue, setFormValue] = useControlField<string>(field)
  const { setFieldTouched } = useFormContext(`contact-drawer-form`)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    setEditValue(value || ``)
  }, [value])

  // Initialize formValue with the original value if not already set
  useEffect(() => {
    if (value) setFormValue(value)
  }, [value])

  // Use form value if available, otherwise use prop value
  // Handle empty string formValue explicitly to allow clearing fields
  // For input fields, show "Set {fieldName}" when empty
  const emptyPlaceholder = `Set ${displayLabel}`
  const displayValue =
    formValue !== undefined ? formValue || emptyPlaceholder : defaultValue || value || emptyPlaceholder

  const handleSave = (): void => {
    // Update form value
    setFormValue(editValue)

    // Mark as touched if different from original
    const originalValue = defaultValue || value
    const isValueChanged = !isEqual(editValue, originalValue)
    if (isValueChanged) {
      setFieldTouched(field, true)
    }

    // Call onChange prop if provided (for backwards compatibility)
    if (onChange) {
      onChange(editValue)
    }
    setIsEditing(false)
  }

  const handleCancel = (): void => {
    setEditValue(value || ``)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === `Enter`) {
      handleSave()
    } else if (e.key === `Escape`) {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className={WRAPPER_CLASS}>
        <Tooltip
          className={`${labelWidth} min-w-0`}
          tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
          message={labelTooltipContent}
          placement="left"
        >
          <div
            className={LABEL_CLASS}
            onMouseOver={(event) => {
              const target = event.currentTarget
              const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
              setLabelTooltipContent(isOverflowing ? displayLabel : undefined)
            }}
            onMouseOut={() => setLabelTooltipContent(undefined)}
          >
            {displayLabel}
          </div>
        </Tooltip>
        <div className="flex h-8 flex-1 cursor-pointer items-center justify-between rounded-md border border-blue-600 bg-surface py-1 pl-1.5 pr-1">
          <input
            ref={inputRef}
            name={field}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={INPUT_EDIT_CLASS}
          />
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              className="size-6 flex shrink-0 items-center justify-center rounded-md border border-default bg-surface"
              onMouseDown={(e) => {
                e.preventDefault()
                handleCancel()
              }}
            >
              <XMarkIcon className="size-4 text-default" />
            </button>
            <button
              type="button"
              className="size-6 flex shrink-0 items-center justify-center rounded-md bg-blue-600"
              onClick={handleSave}
            >
              <CheckIcon className="size-4 text-on-color" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={WRAPPER_CLASS}>
      <input type="hidden" name={field} value={formValue || ``} />
      <Tooltip
        className={`${labelWidth} min-w-0`}
        tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        message={labelTooltipContent}
        placement="left"
      >
        <div
          className={LABEL_CLASS}
          onMouseOver={(event) => {
            const target = event.currentTarget
            const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
            setLabelTooltipContent(isOverflowing ? displayLabel : undefined)
          }}
          onMouseOut={() => setLabelTooltipContent(undefined)}
        >
          {displayLabel}
        </div>
      </Tooltip>
      <div
        role="button"
        className="group relative flex h-8 min-w-0 flex-1 cursor-pointer items-center rounded-md pl-2 pr-2 hover:bg-surface-muted"
        onClick={() => canEdit && setIsEditing(true)}
      >
        <Tooltip
          className={classNames(`min-w-0 flex-1`, canEdit && `!cursor-pointer group-hover:pr-10`)}
          message={tooltipContent}
          tooltipClassName="rounded-md !cursor-pointer bg-inverse px-2 py-1 text-ui-xs text-inverse w-80"
        >
          <div
            className={classNames(
              `min-w-0 truncate text-ui-xs font-semibold`,
              displayValue === emptyPlaceholder ? `text-label-xs italic text-minimal` : className || `text-strong`,
            )}
            onMouseOver={(event) => {
              const target = event.currentTarget
              const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
              setTooltipContent(isOverflowing ? displayValue : undefined)
            }}
            onMouseOut={() => setTooltipContent(undefined)}
          >
            {displayValue}
          </div>
        </Tooltip>
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsEditing(true)
            }}
            className="size-8 absolute right-2 hidden items-center justify-center rounded-lg text-minimal group-hover:flex"
          >
            <EditPencil width={16} height={16} />
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Combo component for select dropdown fields
 */
export function TypedComboV2<T>({
  field,
  label,
  value,
  options,
  emptyMessage,
  canEdit = true,
  onChange,
  isSpotlight = false,
  renderTrigger,
}: {
  field: Extract<keyof T, string>
  label?: string
  value: any
  options: OptionType[]
  emptyMessage?: string
  canEdit?: boolean
  onChange?: (value: string | undefined) => void
  isSpotlight?: boolean
  renderTrigger?: (selectedValue: string | undefined) => ReactNode
}): ReactElement {
  const displayLabel = label || startCase(field)
  const labelWidth = isSpotlight ? `w-[147px]` : `w-[131px]`
  const [labelTooltipContent, setLabelTooltipContent] = useState<string | undefined>(undefined)

  // Form integration when name prop is provided
  const { defaultValue } = useField(field)
  const [formValue, setFormValue] = useControlField<string>(field)
  const { setFieldTouched } = useFormContext(`contact-drawer-form`)

  // Use form value if available, otherwise use prop value
  const currentValue = field ? formValue || defaultValue : value
  const selectedOption = options?.find((option) => option.value === currentValue)
  const [isOpen, setIsOpen] = useState(false)
  const emptyPlaceholder = `Select ${displayLabel}`

  const handleChange = (option: OptionType | undefined): void => {
    const newValue = option?.value

    // Update form if name prop is provided
    if (field && setFormValue) {
      setFormValue(newValue)

      // Only mark as touched if the value is different from the default/original value
      const isValueChanged = !isEqual(newValue, defaultValue) && !isEqual(newValue, value)
      setFieldTouched(field, isValueChanged)
    }

    // Also call onChange prop if provided (for backwards compatibility)
    if (onChange) {
      onChange(newValue)
    }
  }

  const handleButtonClick = (): void => {
    setIsOpen(true)
  }

  return (
    <div className={WRAPPER_CLASS}>
      <Tooltip
        className={`${labelWidth} min-w-0`}
        tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        message={labelTooltipContent}
        placement="left"
      >
        <div
          className={LABEL_CLASS}
          onMouseOver={(event) => {
            const target = event.currentTarget
            const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
            setLabelTooltipContent(isOverflowing ? displayLabel : undefined)
          }}
          onMouseOut={() => setLabelTooltipContent(undefined)}
        >
          {displayLabel}
        </div>
      </Tooltip>
      <div className="flex min-w-0 flex-1">
        <ClickAwayListener onClickAway={() => isOpen && setIsOpen(false)}>
          <div className="w-full">
            <Combo
              disabled={!canEdit}
              options={options}
              value={selectedOption}
              name={field}
              className="w-full"
              chevronClassName="hidden"
              inputClassName={classNames(
                `!h-8 !px-0 !py-0 !text-ui-xs !font-semibold !shadow-none`,
                isOpen ? `!border !border-blue-600 !rounded-md !bg-surface` : `!border-none`,
              )}
              placeholder={selectedOption?.name || value || emptyPlaceholder}
              onChange={handleChange}
              onClearSearch={() => handleChange(undefined)}
              onButtonClick={handleButtonClick}
              customButtonClassName={COMBO_TRIGGER_CLASS}
              customButton={
                renderTrigger?.(selectedOption?.value ?? value) ?? (
                  <div
                    className={classNames(
                      `group flex h-8 w-full cursor-pointer items-center justify-between rounded-md px-2`,
                      isOpen ? `border border-blue-600 bg-surface` : `hover:bg-surface-muted`,
                    )}
                  >
                    <span
                      className={classNames(
                        `min-w-0 truncate text-ui-xs font-semibold`,
                        !selectedOption?.name && !value ? `text-label-xs italic text-minimal` : `text-strong`,
                      )}
                    >
                      {selectedOption?.name || value || emptyPlaceholder}
                    </span>
                    {canEdit && (
                      <div
                        className={classNames(
                          `size-8 flex items-center justify-center`,
                          !isOpen && `opacity-0 group-hover:opacity-100`,
                        )}
                      >
                        <NavArrowDown className={`size-4 ${isOpen ? `text-strong` : `text-minimal`}`} />
                      </div>
                    )}
                  </div>
                )
              }
              useSingleFieldInput
            />
          </div>
        </ClickAwayListener>
      </div>
    </div>
  )
}

/**
 * ComboAsync component for async select dropdown fields
 * Note: Currently simplified due to complex FormComboAsyncV2 type constraints
 */
export function TypedAsyncComboV2<T>({
  field,
  label,
  value,
  emptyMessage,
  canEdit = true,
  isSpotlight = false,
  queryURL,
  additionalParams,
  fetcher,
  optionsMapper,
  onChange,
}: {
  field: Extract<keyof T, string>
  label?: string
  value: any
  emptyMessage?: string
  canEdit?: boolean
  queryURL?: string
  fetcher?: (params: { query: string; [key: string]: string }) => Promise<unknown>
  additionalParams?: Record<string, string>
  optionsMapper: (res: any) => OptionType[]
  onChange?: (value: OptionType | undefined) => void
  isSpotlight?: boolean
}): ReactElement {
  const displayLabel = label || startCase(field)
  const labelWidth = isSpotlight ? `w-[147px]` : `w-[131px]`
  const [labelTooltipContent, setLabelTooltipContent] = useState<string | undefined>(undefined)
  const [valueTooltipContent, setValueTooltipContent] = useState<string | undefined>(undefined)

  // Form integration when name prop is provided
  const { defaultValue } = useField(field)
  const [formValue, setFormValue] = useControlField<OptionType>(field)
  const { setFieldTouched } = useFormContext(`contact-drawer-form`)

  // Use form value if available, otherwise use prop value
  const currentValue = field ? formValue || defaultValue : value
  const [isOpen, setIsOpen] = useState(false)
  const emptyPlaceholder = `Select ${displayLabel}`

  const handleChange = (option: OptionType | undefined): void => {
    // Update form if name prop is provided
    if (field && setFormValue && option) {
      setFormValue(option)

      // Only mark as touched if the value is different from the default/original value
      const isValueChanged = !isEqual(option, defaultValue) && !isEqual(option, value)
      setFieldTouched(field, isValueChanged)
    }

    // Also call onChange prop if provided (for backwards compatibility)
    if (onChange) {
      onChange(option)
    }
  }

  const handleButtonClick = (): void => {
    setIsOpen(true)
  }

  return (
    <div className={WRAPPER_CLASS}>
      <Tooltip
        className={`${labelWidth} min-w-0`}
        tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        message={labelTooltipContent}
        placement="left"
      >
        <div
          className={LABEL_CLASS}
          onMouseOver={(event) => {
            const target = event.currentTarget
            const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
            setLabelTooltipContent(isOverflowing ? displayLabel : undefined)
          }}
          onMouseOut={() => setLabelTooltipContent(undefined)}
        >
          {displayLabel}
        </div>
      </Tooltip>
      <div className="flex min-w-0 flex-1">
        <ClickAwayListener onClickAway={() => isOpen && setIsOpen(false)}>
          <div className="w-full">
            <ComboAsync
              disabled={!canEdit}
              value={currentValue}
              name={field}
              className="w-full"
              chevronClassName="hidden"
              inputClassName={classNames(
                `!h-8 !px-0 !py-0 !text-ui-xs !font-semibold !shadow-none`,
                isOpen ? `!border !border-blue-600 !rounded-md !bg-surface` : `!border-none`,
              )}
              placeholder={currentValue?.name || value?.name || emptyPlaceholder}
              onChange={handleChange}
              onClearSearch={() => handleChange(undefined)}
              onButtonClick={handleButtonClick}
              customButtonClassName={COMBO_TRIGGER_CLASS}
              queryURL={queryURL}
              fetcher={fetcher}
              additionalParams={additionalParams}
              optionsMapper={optionsMapper}
              customButton={
                <div
                  className={classNames(
                    `group flex h-8 w-full cursor-pointer items-center justify-between overflow-hidden rounded-md px-2`,
                    isOpen ? `border border-blue-600 bg-surface` : `hover:bg-surface-muted`,
                  )}
                >
                  <Tooltip
                    className="min-w-0 flex-1"
                    tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse !cursor-pointer"
                    message={valueTooltipContent}
                  >
                    <div
                      className={classNames(
                        `min-w-0 cursor-pointer truncate text-ui-xs font-semibold`,
                        !currentValue?.name && !value?.name ? `text-label-xs italic text-minimal` : `text-strong`,
                      )}
                      onMouseOver={(event) => {
                        const target = event.currentTarget
                        const isOverflowing = target.scrollWidth > target.clientWidth
                        const displayName = currentValue?.name || value?.name
                        setValueTooltipContent(isOverflowing ? displayName : undefined)
                      }}
                      onMouseOut={() => setValueTooltipContent(undefined)}
                    >
                      {currentValue?.name || value?.name || emptyPlaceholder}
                    </div>
                  </Tooltip>
                  {canEdit && (
                    <div
                      className={classNames(
                        `size-8 flex items-center justify-center`,
                        !isOpen && `opacity-0 group-hover:opacity-100`,
                      )}
                    >
                      <NavArrowDown className={`size-4 ${isOpen ? `text-strong` : `text-minimal`}`} />
                    </div>
                  )}
                </div>
              }
              useSingleFieldInput={false}
            />
          </div>
        </ClickAwayListener>
      </div>
    </div>
  )
}
